import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import ComicCard from '@/components/feature/ComicCard';
import { useSEO } from '@/hooks/useSEO';

const PAGE_SIZE = 8;
const statusOptions = ['全部', '连载', '完结'];
const sortOptions = [
  { value: 'update', label: '最新更新', icon: 'ri-time-line' },
  { value: 'popular', label: '热门程度', icon: 'ri-fire-line' },
  { value: 'rating', label: '评分最高', icon: 'ri-star-line' },
];

export default function Category() {
  const { categoryName } = useParams();
  const category = categories.find((item) => item.slug === categoryName);

  const [status, setStatus] = useState('全部');
  const [year, setYear] = useState('全部');
  const [tag, setTag] = useState('全部');
  const [sort, setSort] = useState('update');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const categoryComics = useMemo(
    () => comics.filter((comic) => comic.category === categoryName),
    [categoryName],
  );

  const allTags = useMemo(() => {
    const set = new Set<string>();
    categoryComics.forEach((comic) => comic.tags.forEach((item) => set.add(item)));
    return Array.from(set);
  }, [categoryComics]);

  const allYears = useMemo(() => {
    const set = new Set<number>();
    categoryComics.forEach((comic) => set.add(comic.year));
    return Array.from(set).sort((a, b) => b - a);
  }, [categoryComics]);

  useEffect(() => {
    setStatus('全部');
    setYear('全部');
    setTag('全部');
    setSort('update');
    setPage(1);
    setFilterOpen(false);
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [status, year, tag, sort]);

  const filtered = useMemo(() => {
    let list = categoryComics.filter((comic) => {
      if (status !== '全部' && comic.status !== status) return false;
      if (year !== '全部' && String(comic.year) !== year) return false;
      if (tag !== '全部' && !comic.tags.includes(tag)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === 'popular') return b.popularity - a.popularity;
      if (sort === 'rating') return b.rating - a.rating;
      return b.updateDate.localeCompare(a.updateDate);
    });
    return list;
  }, [categoryComics, status, year, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedList = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hasFilter = status !== '全部' || year !== '全部' || tag !== '全部';

  const resetFilters = () => {
    setStatus('全部');
    setYear('全部');
    setTag('全部');
    setSort('update');
  };

  useSEO({
    title: `${category ? category.name : '分类'} - 免费双女主韩漫在线阅读`,
    description: category
      ? `${category.name}双女主韩漫分类：${category.description} 提供免费在线阅读与最新更新，快速找到喜欢的双女主韩漫作品。`
      : '双女主韩漫分类列表，提供免费的在线阅读体验。',
    keywords: category
      ? `${category.name},双女主韩漫,韩漫免费观看,${category.name}漫画,在线阅读`
      : '双女主韩漫,分类,在线阅读',
  });

  const filterPanel = (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-foreground-950">
          <i className="ri-file-list-2-line text-primary-500"></i>
          连载状态
        </h3>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setStatus(option)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                status === option
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-foreground-950">
          <i className="ri-price-tag-3-line text-primary-500"></i>
          漫画题材
        </h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...allTags].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setTag(option)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                tag === option
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-800'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-foreground-950">
          <i className="ri-calendar-line text-primary-500"></i>
          更新年份
        </h3>
        <div className="flex flex-wrap gap-2">
          {['全部', ...allYears.map(String)].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setYear(option)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                year === option
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={resetFilters}
        disabled={!hasFilter}
        className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
      >
        <i className="ri-refresh-line"></i>
        重置筛选条件
      </button>
    </div>
  );

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-6 flex flex-wrap items-center gap-1 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{category ? category.name : '分类'}</span>
      </nav>

      <div className="mb-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-40 w-full md:h-48">
          {category && (
            <img
              src={category.cover}
              alt={`${category.name}双女主韩漫分类`}
              title={`${category.name}双女主韩漫免费阅读`}
              className="h-full w-full object-cover object-top"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/25"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
            <span className="mb-2 flex w-fit items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className={category ? category.icon : 'ri-layout-grid-line'}></i>
              题材分类
            </span>
            <h1 className="text-2xl font-bold text-background-50 md:text-4xl">
              {category ? category.name : '未找到分类'}双女主韩漫
            </h1>
            <p className="mt-2 max-w-2xl text-xs leading-relaxed text-background-200 md:text-sm">
              {category ? category.description : '该分类暂不存在，请从导航栏或首页重新选择。'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-5 flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-filter-3-line text-primary-500"></i>
              筛选条件
            </h2>
            {filterPanel}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-file-list-3-line text-accent-600"></i>
              共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-medium text-foreground-700 lg:hidden cursor-pointer"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
              <div className="flex items-center gap-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSort(option.value)}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                      sort === option.value
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={option.icon}></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filterOpen && (
            <div className="mb-4 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
              {filterPanel}
            </div>
          )}

          {pagedList.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-3">
                {pagedList.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item)}
                      className={`flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors cursor-pointer ${
                        page === item
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <div>
                <h3 className="text-lg font-bold text-foreground-950">暂无符合条件的漫画</h3>
                <p className="mt-1 text-sm text-foreground-500">
                  试试调整或重置筛选条件，发现更多双女主韩漫
                </p>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}